import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CP, CapCard, GaugeDonut, Pill, priorityTone } from "./ui";
import { GraduationCap, UsersFour, ArrowsLeftRight, Handshake } from "@phosphor-icons/react";

const REC_ICONS = [GraduationCap, UsersFour, ArrowsLeftRight, Handshake];

export default function GapAnalysis() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/capability/gaps/analysis").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm py-10 text-center" style={{ color: CP.sub }}>Analyzing capability gaps…</div>;
  const gaugeColor = data.overall_score_pct >= 60 ? CP.red : data.overall_score_pct >= 30 ? CP.amber : CP.green;

  return (
    <div className="space-y-4" data-testid="gap-analysis">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CapCard title="Overall Gap Score" subtitle="Current state vs target state" testid="gap-gauge">
          <div className="flex flex-col items-center pt-2">
            <GaugeDonut value={data.overall_score_pct} max={100} label={data.overall_label} color={gaugeColor} size={170} />
            <div className="grid grid-cols-3 gap-2 w-full mt-4 text-center">
              {["high", "medium", "low"].map((p) => (
                <div key={p} className="rounded-lg py-2" style={{ border: `1px solid ${CP.border}` }}>
                  <div className="text-lg font-bold" style={{ color: p === "high" ? CP.red : p === "medium" ? CP.amber : CP.green }}>
                    {data.priority_counts[p] || 0}
                  </div>
                  <div className="text-[10px] capitalize" style={{ color: CP.sub }}>{p} priority</div>
                </div>
              ))}
            </div>
          </div>
        </CapCard>

        <div className="lg:col-span-2 rounded-xl bg-white shadow-sm overflow-x-auto" style={{ border: `1px solid ${CP.border}` }} data-testid="gap-table">
          <div className="px-4 pt-4 pb-2 text-sm font-semibold" style={{ color: CP.text }}>Top Capability Gaps</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider" style={{ color: CP.sub, borderBottom: `1px solid ${CP.border}` }}>
                <th className="text-left py-2 px-4">Capability</th>
                <th className="text-right py-2 px-3">Current State</th>
                <th className="text-right py-2 px-3">Target State</th>
                <th className="text-right py-2 px-3">Gap</th>
                <th className="text-left py-2 px-4">Priority</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.skill} style={{ borderBottom: `1px solid ${CP.border}` }} data-testid={`gap-row-${r.skill}`}>
                  <td className="py-2.5 px-4 capitalize font-medium" style={{ color: CP.text }}>{r.skill}</td>
                  <td className="py-2.5 px-3 text-right" style={{ color: CP.text }}>{r.current.toFixed(1)}</td>
                  <td className="py-2.5 px-3 text-right" style={{ color: CP.text }}>{r.target.toFixed(1)}</td>
                  <td className="py-2.5 px-3 text-right font-bold" style={{ color: r.gap <= -1.2 ? CP.red : r.gap <= -0.6 ? CP.amber : CP.green }}>{r.gap.toFixed(1)}</td>
                  <td className="py-2.5 px-4"><Pill tone={priorityTone(r.priority)}>{r.priority.charAt(0).toUpperCase() + r.priority.slice(1)}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-4 px-4 py-3 text-[11px]" style={{ color: CP.sub }}>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: CP.red }} />High (≤ -1.2)</span>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: CP.amber }} />Medium (-0.6 to -1.2)</span>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: CP.green }} />Low (&gt; -0.6)</span>
          </div>
        </div>
      </div>

      <CapCard title="Recommendations" testid="gap-recommendations">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.recommendations.map((rec, i) => {
            const Icon = REC_ICONS[i % REC_ICONS.length];
            return (
              <div key={i} className="rounded-lg p-3 flex gap-3" style={{ border: `1px solid ${CP.border}` }} data-testid={`gap-rec-${i}`}>
                <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F3F0FF" }}>
                  <Icon size={18} color={CP.purple} weight="duotone" />
                </span>
                <div>
                  <div className="text-sm font-semibold capitalize" style={{ color: CP.text }}>{rec.title}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: CP.sub }}>{rec.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      </CapCard>
    </div>
  );
}
